import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "wouter";
import { ArrowRight, Home, Users, Compass, BarChart3, Mail, Search, FileText, BookOpen } from "lucide-react";
import { cases } from "@/data/cases";
import { articles } from "@/data/articles";
import logoWrkt from "@assets/Wrkt_-_PNG_1771840403931.png";

export default function SitemapVisual() {
  const { language, localePath } = useLanguage();
  const nl = language === "nl";

  const mainPages = [
    {
      icon: Home,
      path: "/",
      title: "Home",
      description: nl
        ? "Landingspagina met hero, social proof, 3-staps proces, offer CTA en FAQ"
        : "Landing page with hero, social proof, 3-step process, offer CTA and FAQ",
      purpose: nl
        ? "Wat we doen + bewijs. Eerste indruk en conversie."
        : "What we do + proof. First impression and conversion.",
    },
    {
      icon: Users,
      path: "/about",
      title: nl ? "Over Ons" : "About Us",
      description: nl
        ? "Team, AI-tooling, vergelijkingstabel (Wrkt vs traditioneel), Amsterdam locatie"
        : "Team, AI tooling, comparison table (Wrkt vs traditional), Amsterdam location",
      purpose: nl
        ? "Wie we zijn. Vertrouwen opbouwen."
        : "Who we are. Build trust.",
    },
    {
      icon: Compass,
      path: "/werkwijze",
      title: nl ? "Werkwijze" : "Method",
      description: nl
        ? "Growth Compass methode: Intelligence, Creation, Execution, Retention. 4 pijnpunten, video showcase"
        : "Growth Compass method: Intelligence, Creation, Execution, Retention. 4 pain points, video showcase",
      purpose: nl
        ? "Hoe we werken. Methodologie uitleggen."
        : "How we work. Explain methodology.",
    },
    {
      icon: BarChart3,
      path: "/cases",
      title: "Cases",
      description: nl
        ? `${cases.length} case studies, filterbaar op vertical en dienst. Resultaten met cijfers.`
        : `${cases.length} case studies, filterable by vertical and service. Results with numbers.`,
      purpose: nl
        ? "Portfolio en bewijs. Resultaten tonen."
        : "Portfolio and proof. Show results.",
      children: cases.map((c) => ({
        path: `/cases/${c.slug}`,
        title: c.name,
        services: c.services.join(", "),
      })),
    },
    {
      icon: BookOpen,
      path: "/artikelen",
      title: nl ? "Artikelen" : "Articles",
      description: nl
        ? `${articles.length} thought leadership artikelen over D2C, branding, performance marketing en data-driven strategie. Door Bas Lens en Robin de Wildt.`
        : `${articles.length} thought leadership articles on D2C, branding, performance marketing and data-driven strategy. By Bas Lens and Robin de Wildt.`,
      purpose: nl
        ? "SEO en autoriteit. Expertise tonen."
        : "SEO and authority. Demonstrate expertise.",
      children: articles
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map((a) => ({
          path: `/artikelen/${a.slug}`,
          title: a.title,
          services: `${a.author} - ${a.date}`,
        })),
    },
    {
      icon: Search,
      path: "/scan",
      title: "Growth Scan",
      description: nl
        ? "Interactieve quiz (5 categorieen): Advertising, Social Media, Tracking, Technisch, SEO. Lead capture via e-mail."
        : "Interactive quiz (5 categories): Advertising, Social Media, Tracking, Technical, SEO. Lead capture via email.",
      purpose: nl
        ? "Lead generatie. Gratis waarde bieden."
        : "Lead generation. Offer free value.",
    },
    {
      icon: Mail,
      path: "/contact",
      title: "Contact",
      description: nl
        ? "Contactformulier met proces-timeline. Opslag in PostgreSQL + HubSpot. Robin de Wildt als contactpersoon."
        : "Contact form with process timeline. Storage in PostgreSQL + HubSpot. Robin de Wildt as contact person.",
      purpose: nl
        ? "Conversie. Direct contact opnemen."
        : "Conversion. Get in touch directly.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white">
      <div className="container mx-auto px-6 md:px-12 py-12 max-w-5xl">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <img src={logoWrkt} alt="Wrkt Digital" className="h-8 object-contain" />
            <div>
              <h1 className="text-2xl font-display font-bold">Sitemap</h1>
              <p className="text-white/50 text-sm">wrkt.nl</p>
            </div>
          </div>
          <Link href={localePath("/")} className="text-sm text-white/50 hover:text-[#4CEAAC] transition-colors flex items-center gap-1">
            {nl ? "Terug naar site" : "Back to site"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mb-8 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="font-display font-bold text-lg mb-2">{nl ? "Overzicht" : "Overview"}</h2>
          <p className="text-white/60 text-sm leading-relaxed">
            {nl
              ? `Wrkt Digital - AI-first digital marketing bureau Amsterdam. ${mainPages.length} hoofdpagina's + ${cases.length} individuele case pagina's. Tweetalig (NL/EN) met /en/ prefix voor Engelse versies.`
              : `Wrkt Digital - AI-first digital marketing agency Amsterdam. ${mainPages.length} main pages + ${cases.length} individual case pages. Bilingual (NL/EN) with /en/ prefix for English versions.`}
          </p>
        </div>

        <div className="space-y-4">
          {mainPages.map((page) => (
            <div key={page.path} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#256C43]/30 border border-[#4CEAAC]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <page.icon className="w-5 h-5 text-[#4CEAAC]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-display font-bold text-lg">{page.title}</h3>
                      <code className="text-xs text-[#4CEAAC]/70 bg-[#4CEAAC]/10 px-2 py-0.5 rounded font-mono">
                        {page.path}
                      </code>
                    </div>
                    <p className="text-white/60 text-sm mb-2">{page.description}</p>
                    <p className="text-white/40 text-xs">
                      <span className="text-[#4CEAAC]/60 font-semibold">{nl ? "Doel" : "Purpose"}:</span> {page.purpose}
                    </p>
                  </div>
                </div>
              </div>

              {page.children && (
                <div className="border-t border-white/5 bg-white/[0.02] px-6 py-4">
                  <p className="text-xs text-white/30 uppercase tracking-widest font-bold mb-3">
                    {nl ? `${page.children.length} subpagina's` : `${page.children.length} subpages`}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {page.children.map((child) => (
                      <div
                        key={child.path}
                        className="flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg bg-white/5"
                      >
                        <FileText className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-white/80 text-xs font-medium">{child.title}</span>
                          <span className="text-white/30 text-[10px] block truncate">{child.services}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="font-display font-bold text-lg mb-4">{nl ? "Navigatiestructuur" : "Navigation Structure"}</h2>
          <div className="text-sm text-white/60 space-y-2">
            <p><span className="text-white/80 font-medium">Navbar:</span> Logo, Cases, Over Ons, Werkwijze, Contact + NL/EN toggle + Growth Scan CTA</p>
            <p><span className="text-white/80 font-medium">Footer:</span> Cases, Artikelen, Over Ons, Werkwijze, Contact, LinkedIn, Instagram, Privacy</p>
            <p><span className="text-white/80 font-medium">{nl ? "Taalrouting" : "Language routing"}:</span> NL = /page, EN = /en/page</p>
          </div>
        </div>

        <div className="mt-8 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="font-display font-bold text-lg mb-4">{nl ? "Technische SEO bestanden" : "Technical SEO files"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <a href="/sitemap.xml" target="_blank" className="flex items-center gap-2 text-white/60 hover:text-[#4CEAAC] transition-colors">
              <FileText className="w-4 h-4" /> sitemap.xml
            </a>
            <a href="/robots.txt" target="_blank" className="flex items-center gap-2 text-white/60 hover:text-[#4CEAAC] transition-colors">
              <FileText className="w-4 h-4" /> robots.txt
            </a>
            <a href="/llms.txt" target="_blank" className="flex items-center gap-2 text-white/60 hover:text-[#4CEAAC] transition-colors">
              <FileText className="w-4 h-4" /> llms.txt
            </a>
          </div>
        </div>

        <p className="text-center text-white/20 text-xs mt-12">
          Wrkt Digital - wrkt.nl
        </p>
      </div>
    </div>
  );
}
